import { useCallback, useEffect, useMemo, useState } from "react";
import { Chip, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  MoreVert,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import {
  deleteConnector,
  fetchConnectors,
} from "@accredo/fe-shared/services/importConnectorService.js";
import ImportConnectorFormModal from "../ImportConnectorFormModal.jsx";

export default function ImportConnectorsTable() {
  const { enqueueSnackbar } = useSnackbar();
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [rowMenuState, setRowMenuState] = useState({ anchorEl: null, menuRowId: null });

  const load = useCallback(() => {
    setLoading(true);
    fetchConnectors()
      .then((res) => {
        setConnectors(res.data ?? []);
        setHasLoaded(true);
      })
      .catch(() =>
        enqueueSnackbar("Connectoren konnten nicht geladen werden", {
          variant: "error",
        })
      )
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  function openCreate() {
    setEditingId(null);
    setModalOpen(true);
  }
  function openEdit(row) {
    setEditingId(row.id);
    setModalOpen(true);
  }
  function handleModalClose(saved) {
    setModalOpen(false);
    if (saved) load();
  }

  function handleMenuOpen(e, id) {
    e.stopPropagation();
    setRowMenuState({ anchorEl: e.currentTarget, menuRowId: id });
  }
  function handleMenuClose(e) {
    e?.stopPropagation();
    setRowMenuState({ anchorEl: null, menuRowId: null });
  }

  async function handleDelete(e, row) {
    handleMenuClose(e);
    if (!window.confirm(`Connector „${row.name}" wirklich löschen?`)) return;
    try {
      await deleteConnector(row.id);
      enqueueSnackbar("Connector gelöscht", { variant: "success" });
      load();
    } catch {
      enqueueSnackbar("Connector konnte nicht gelöscht werden", { variant: "error" });
    }
  }

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
      },
      {
        accessorKey: "slug",
        header: "Slug",
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "description",
        header: "Beschreibung",
        Cell: ({ cell }) => cell.getValue() ?? "—",
      },
      {
        accessorKey: "is_active",
        header: "Aktiv",
        size: 90,
        Cell: ({ cell }) => (
          <Chip
            size="small"
            label={cell.getValue() ? "Aktiv" : "Inaktiv"}
            color={cell.getValue() ? "success" : "default"}
          />
        ),
      },
      {
        id: "actions",
        header: "",
        size: 56,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnHiding: false,
        Cell: ({ row }) => (
          <>
            <IconButton size="small" onClick={(e) => handleMenuOpen(e, row.original.id)}>
              <MoreVert fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={rowMenuState.anchorEl}
              open={Boolean(rowMenuState.anchorEl) && rowMenuState.menuRowId === row.original.id}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={(e) => handleDelete(e, row.original)}>
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                Löschen
              </MenuItem>
            </Menu>
          </>
        ),
      },
    ],
    [rowMenuState]
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={connectors}
        isLoading={loading}
        hasLoaded={hasLoaded}
        manualSorting={false}
        manualFiltering={false}
        enableGlobalFilter={true}
        enableColumnFilters={false}
        onRowClick={openEdit}
        toolbarActions={[
          {
            type: "button",
            label: "Neu",
            icon: <AddIcon />,
            onClick: openCreate,
            variant: "primary",
          },
        ]}
      />

      <ImportConnectorFormModal
        open={modalOpen}
        onClose={handleModalClose}
        connectorId={editingId}
      />
    </>
  );
}
